<template>
  <div 
    class="notification-history-item"
    :class="notificationClass"
    :tabindex="0"
    role="listitem"
    :aria-label="accessibilityLabel"
    @mouseenter="pauseTimer"
    @mouseleave="resumeTimer"
    @focus="pauseTimer"
    @blur="resumeTimer"
  >
    <!-- Simple timestamp -->
    <div class="notification-history-item__header">
      <div class="notification-history-item__timestamp">
        {{ relativeTime }}
      </div>
    </div>

    <!-- Use the exact same NotificationContent component as overlay notifications -->
    <NotificationContent
      :notification="notification"
      @expand="handleExpand"
      @collapse="handleCollapse"
      @close="handleClose"
      @action="handleNotificationAction"
      class="notification-history-item__content"
    />
    
    <!-- Timer Progress Indicator -->
    <div
      v-if="showTimerProgress"
      :class="timerProgressClass"
      :style="{ transform: `scaleX(${timerProgressScale})` }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import NotificationContent from './NotificationContent.vue';
import { useNotifications } from '@/composables/useNotifications';
import type { ToastNotification } from '@/types/notifications';

export interface NotificationHistoryItemProps {
  notification: ToastNotification;
}

const props = defineProps<NotificationHistoryItemProps>();

const emit = defineEmits<{
  remove: [id: string];
  action: [notificationId: string, actionId: string];
  expand: [id: string];
  collapse: [id: string];
}>();

const { pauseNotificationTimeout, resumeNotificationTimeout, getNotificationRemainingTime } = useNotifications();

// Computed properties
const notificationClass = computed(() => [
  `notification-history-item--${props.notification.severity}`,
  {
    'notification-history-item--expanded': props.notification.expanded,
    'notification-history-item--paused': isPaused.value,
  }
]);

const timerProgressClass = computed(() => [
  'notification-history-item__timer-progress',
  `notification-history-item__timer-progress--${props.notification.severity}`
]);

const accessibilityLabel = computed(() => {
  const parts = [
    `${props.notification.severity} notification`,
    props.notification.message.text,
    relativeTime.value
  ];
  return parts.join(', ');
});

const relativeTime = computed(() => {
  return formatRelativeTime(props.notification.originalCreatedAt);
});

// Timer progress for active notifications with timeout
const isPaused = ref(false);
const pausedAt = ref(0);
const animationTick = ref(0); // Reactive trigger for animation updates
let animationFrameId: number | null = null;

const showTimerProgress = computed(() => {
  if (!props.notification.timeout || 
      props.notification.timeout <= 0 ||
      props.notification.sticky) {
    return false;
  }
  
  // Show timer if notification still has remaining time (regardless of status)
  const remaining = getNotificationRemainingTime(props.notification.id);
  return remaining > 0;
});

const timerProgressScale = computed(() => {
  if (!showTimerProgress.value || !props.notification.timeout) return 1;
  // Include animationTick in dependency to trigger reactivity
  animationTick.value; // This creates a reactive dependency
  const remaining = getNotificationRemainingTime(props.notification.id);
  return Math.max(0, remaining / props.notification.timeout);
});

// Initialize timer for active notifications
onMounted(() => {
  if (showTimerProgress.value && props.notification.timeout) {
    startTimerAnimation();
  }
});

onUnmounted(() => {
  stopTimerAnimation();
  // Make sure to resume the central timer if we were paused
  if (isPaused.value) {
    resumeNotificationTimeout(props.notification.id);
  }
});

const startTimerAnimation = () => {
  if (!showTimerProgress.value || !props.notification.timeout) return;
  
  const updateTimer = () => {
    if (props.notification.status === 'active' && props.notification.timeout) {
      // Always continue the animation loop
      animationFrameId = requestAnimationFrame(updateTimer);
      
      if (!isPaused.value) {
        // Trigger reactivity for timerProgressScale computed only when not paused
        animationTick.value++;
        
        // Check if we should stop (when time runs out)
        const remaining = getNotificationRemainingTime(props.notification.id);
        if (remaining <= 0) {
          stopTimerAnimation();
        }
      }
      // When paused, we keep the loop running but don't update the tick
    } else {
      stopTimerAnimation();
    }
  };
  updateTimer();
};

const stopTimerAnimation = () => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
};

const pauseTimer = () => {
  if (!showTimerProgress.value || isPaused.value) return;
  
  isPaused.value = true;
  pausedAt.value = Date.now();
  stopTimerAnimation();
  
  // Pause the central notification timeout
  pauseNotificationTimeout(props.notification.id);
};

const resumeTimer = () => {
  if (!showTimerProgress.value || !isPaused.value) return;
  
  // Resume the central notification timeout FIRST
  resumeNotificationTimeout(props.notification.id);
  
  isPaused.value = false;
  
  // Force an immediate animation tick update to resume the visual progress
  animationTick.value++;
  
  // Restart the animation loop since it was stopped in pauseTimer
  startTimerAnimation();
};

// Helper function to format relative time
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  
  if (diffSecs < 60) {
    return diffSecs < 5 ? 'Just now' : `${diffSecs} seconds ago`;
  } else if (diffMins < 60) {
    return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`;
  } else if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  } else if (diffDays < 7) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  } else if (diffWeeks < 4) {
    return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`;
  } else {
    return date.toLocaleDateString();
  }
}

// Event handlers
const handleExpand = () => {
  emit('expand', props.notification.id);
};

const handleCollapse = () => {
  emit('collapse', props.notification.id);
};

const handleClose = () => {
  // For history items, close action should remove them
  emit('remove', props.notification.id);
};

const handleNotificationAction = (actionId: string) => {
  emit('action', props.notification.id, actionId);
};
</script>

<style scoped lang="scss">
.notification-history-item {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-xs);
  margin-bottom: var(--spacing-xs);
  background: var(--vscode-list-inactiveSelectionBackground);
  border-radius: var(--border-radius);
  border-left: 3px solid transparent;
  transition: all 0.15s ease;
  
  &:hover {
    background: var(--vscode-list-hoverBackground);
  }
  
  &:focus {
    outline: 1px solid var(--vscode-focusBorder);
    outline-offset: -1px;
  }
  
  &--unread {
    border-left-color: var(--vscode-button-background);
    
    .notification-history-item__unread-indicator {
      opacity: 1;
    }
  }
  
  // Severity styles matching notification styles
  &--info {
    border-left-color: var(--vscode-notificationsInfoIcon-foreground, #007acc);
  }
  
  &--warning {
    border-left-color: var(--vscode-notificationsWarningIcon-foreground, #ff8c00);
  }
  
  &--error {
    border-left-color: var(--vscode-notificationsErrorIcon-foreground, #f14c4c);
  }
  
  &--success {
    border-left-color: var(--vscode-terminal-ansiGreen, #00bc00);
  }
}

.notification-history-item__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-md) var(--spacing-sm);
}

.notification-history-item__timestamp {
  font-size: 11px;
  color: var(--vscode-descriptionForeground);
  opacity: 0.8;
}

.notification-history-item__unread-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--vscode-button-background);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.notification-history-item__content {
  // Let NotificationContent handle its own styling
  // Just ensure proper spacing
  :deep(.notification-content) {
    background: transparent;
    border: none;
    box-shadow: none;
    margin: 0;
    padding: 0;
  }
}

.notification-history-item__timer-progress {
  position: relative;
  height: 2px;
  background: var(--vscode-button-background);
  margin-top: var(--spacing-xs);
  border-radius: 1px;
  transform-origin: left;
  transition: transform 0.1s linear;
  opacity: 0.7;
  
  &--success {
    background: var(--notification-success);
  }
  
  &--info {
    background: var(--notification-info);
  }
  
  &--warning {
    background: var(--notification-warning);
  }
  
  &--error {
    background: var(--notification-error);
  }
}

.notification-history-item--paused {
  .notification-history-item__timer-progress {
    animation: pulse 2s infinite;
  }
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.7;
  }
  50% {
    opacity: 1;
  }
}
</style>
